import { ComponentStyleSource, getComponentStyleSource } from '@wix/a11y-adapters';
import { createEntityItemId } from '@wix/a11y-rules';
import { Box, Divider } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { rescanTriggerGroupIdAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { ColorContrastRuleFixer } from '../../components/fixers/color-contrast-fixer/color-contrast-rule-fixer';
import { sessionElementsFamily } from '../../components/fixers/color-contrast-fixer/hooks/use-get-color-contrast-elements-in-session';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { STUDIO_COMP_REF_TYPE } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useGenericElement } from '../../hooks/use-generic-element';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
import { getUpdatedColorContrastElementsFromState } from './get-updated-color-contrast-elements-from-state';
export const useColorContrastRule = () => {
  const {
    t
  } = useTranslation();
  const {
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const {
    isGenericElement
  } = useGenericElement({
    getComponentStyleSource,
    pageId: '',
    unknownValue: ComponentStyleSource.UNKNOWN
  });
  const getSessionElementsByPageId = useAtomCallback(useCallback((get, _set, pageId) => {
    return get(sessionElementsFamily(pageId));
  }, []));
  const compactPanelChrome = capabilities.compactPanelChrome;
  const setRescanTriggerGroupId = useSetAtom(rescanTriggerGroupIdAtom);
  const {
    getRuleState,
    panelState,
    getGroupData,
    setRuleState
  } = usePanelStateAtom();
  const transformToUiElements = useCallback(elements => elements.map(el => ({
    ...el,
    uiElementId: createEntityItemId([el.elementId], (el == null ? void 0 : el.itemId) ?? '')
  })), []);
  const getColorContrast = useCallback(_ref => {
    var _ruleState$state, _getGroupData;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const {
      result,
      ruleId,
      metadata
    } = ruleExecutionResult;
    const ruleState = getRuleState(routerId ?? pageId, ruleId);
    const stateElements = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.changedElements) ?? new Map();
    const sessionElements = getSessionElementsByPageId(routerId ?? pageId) ?? new Map();
    const {
      failedElements: initialFailedElements,
      passedElements: initialPassedElements
    } = result;
    const breakpointId = (metadata == null ? void 0 : metadata.breakpointId) ?? STUDIO_COMP_REF_TYPE;
    const passedElements = [...Array.from(stateElements.values()).filter(el => el.isValid), ...transformToUiElements(initialPassedElements ?? [])];
    const passedElementIds = new Set(passedElements.map(el => el.elementId));
    const filteredInitialFailedElements = (initialFailedElements ?? []).filter(failedElement => !passedElementIds.has(failedElement.elementId));
    const failedElements = transformToUiElements(filteredInitialFailedElements);
    const {
      passedElementsWithState,
      failedElementsWithState
    } = getUpdatedColorContrastElementsFromState([...passedElements, ...failedElements], panelState.globalSectionComponentsDataMap);
    const failedElementCount = (failedElementsWithState == null ? void 0 : failedElementsWithState.length) || 0;
    const elementCount = ((passedElementsWithState == null ? void 0 : passedElementsWithState.length) || 0) + failedElementCount;
    const isGenericColorContrastElement = el => {
      var _el$affectingAncestor;
      return isGenericElement(el.elementId, el.className) || (((_el$affectingAncestor = el.affectingAncestors) == null ? void 0 : _el$affectingAncestor.some(_ref2 => {
        let [id,, ancestorClassName] = _ref2;
        return isGenericElement(id, ancestorClassName, true);
      })) ?? true);
    };
    const allColorContrastElements = [...passedElementsWithState, ...failedElementsWithState];
    const manualElementCount = allColorContrastElements.filter(isGenericColorContrastElement).length;
    const manualFailedElementCount = failedElementsWithState.filter(isGenericColorContrastElement).length;
    const uiStatus = failedElementsWithState.length === 0 ? RuleListStatus.passed : RuleListStatus.failed;
    const isRuleResolved = uiStatus !== RuleListStatus.failed;
    const analysisState = (_getGroupData = getGroupData(routerId ?? pageId)) == null ? void 0 : _getGroupData.analysisState;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, ruleId, {
        status: uiStatus
      });
    }
    const hasElementsFixedInSession = sessionElements.size > 0;
    const allElementsNowPassed = isRuleResolved && hasElementsFixedInSession && failedElementsWithState.length === 0;
    const getDescriptionI18nKey = () => {
      if (!isRuleResolved) {
        return 'accessibility-wizard.rules-list.color-contrast.description.failed';
      }
      if (allElementsNowPassed) {
        return 'accessibility-wizard.rules-list.color-contrast.description.passed';
      }
      if (passedElementsWithState.length === 0) {
        return 'accessibility-wizard.rules-list.color-contrast.list.completed.header';
      }
      return 'accessibility-wizard.rules-list.color-contrast.description.partial-pass-with-fixed-rules-in-session';
    };
    const showPassedStateDivider = isRuleResolved && compactPanelChrome && elementCount > 0;
    const ruleLayoutGap = compactPanelChrome && !showPassedStateDivider ? 'SP4' : 'SP2';
    return {
      description: /*#__PURE__*/React.createElement(Box, {
        direction: "vertical",
        gap: "SP2"
      }, /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: getDescriptionI18nKey(),
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-color-contrast-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.color-contrast.description.learn-more.aria-label'),
        title: isRuleResolved ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }), showPassedStateDivider && /*#__PURE__*/React.createElement(Divider, null)),
      elementCount,
      failedElementCount,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: /*#__PURE__*/React.createElement(ColorContrastRuleFixer, {
          breakpointId: breakpointId,
          failedElements: failedElementsWithState,
          pageId: routerId ?? pageId,
          passedElements: passedElementsWithState
        }),
        design: {
          ruleLayout: {
            gap: ruleLayoutGap
          }
        }
      },
      id: `${isCrossPage ? 'cross-page-' : ''}${routerId ?? pageId}-${ruleId}`,
      label: t('accessibility-wizard.rules-list.color-contrast.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          manualElementCount,
          manualFailedElementCount,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(metadata == null ? void 0 : metadata.itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
          setRescanTriggerGroupId(isCrossPage ? pageId : undefined);
        } else {
          setRescanTriggerGroupId(undefined);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          manualElementCount,
          manualFailedElementCount,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [getRuleState, getSessionElementsByPageId, transformToUiElements, panelState.globalSectionComponentsDataMap, getGroupData, t, compactPanelChrome, setRuleState, openSelectedArticle, editorHost, biLogger, isGenericElement, setCurrentTaskInfo, currentTaskInfo, navigateToPage, setRescanTriggerGroupId]);
  return {
    getColorContrast
  };
};
//# sourceMappingURL=use-color-contrast-rule.js.map